<template>
  <div ref="historyEl" :class="[classes.historyWrapper, 'history']">
    <svg
      v-for="(item, index) in history"
      :key="item"
      :width="size + 4"
      :height="size"
      :class="[classes.historyItem, item]"
    >
      <line
        :x1="index === 0 ? size / 2 : 0"
        :y1="size / 2"
        :x2="size + 4"
        :y2="size / 2"
        :style="`stroke:${borderColor};stroke-width:2`"
      />
      <circle
        :cx="(size + 4) / 2"
        :cy="size / 2"
        :r="(size - 1) / 2"
        :stroke="borderColor"
        stroke-width="1"
        :fill="item === 'l' ? lossColor : item === 'w' ? winColor : drawColor"
      />
      <text x="50%" y="50%" :class="classes.text">
        {{ translation.H2HTexts[item.toUpperCase()] || item.toUpperCase() }}
      </text>
    </svg>
    <svg :width="size" :height="size">
      <line
        :x1="0"
        :y1="size / 2"
        :x2="3"
        :y2="size / 2"
        :style="`stroke:${borderColor};stroke-width:2`"
      />
      <polygon
        :points="`2,${size - 3} 2,3 ${size - 6},${size / 2}`"
        :style="`fill:${borderColor};`"
      />
    </svg>
  </div>
</template>

<script lang="ts">
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";
import { createUseStyles } from "@fresh8/vue-jss/src";
import { computed, defineComponent, PropType } from "vue";
import { getTranslation } from "@fresh8/ad-data-composition/src/lang";
import { composePopoverForm } from "@fresh8/ad-data-composition/src/popoverForm";
import { composeEvent } from "@fresh8/ad-data-composition/src/betData";

const useStyles = createUseStyles({
  historyWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center"
  },
  historyItem: {
    width: props => props.width,
    height: props => props.height
  },
  text: {
    fontSize: props => props.fontSize,
    textAnchor: "middle",
    dominantBaseline: "central",
    fill: props => props.textColor
  }
});

export default defineComponent({
  name: "MatchHistory",
  props: {
    eventId: {
      type: String,
      required: true
    },
    history: {
      type: Array as PropType<string[]>,
      required: true
    },
    size: {
      type: Number,
      required: false,
      default: 13
    },
    fontSize: {
      type: Number,
      required: false,
      default: 7
    }
  },
  setup(props) {
    const event = composeEvent(props.eventId);
    const { adTemplate, getColorStyle } = injectBrand();
    const translation = getTranslation(window.navigator.language.toLowerCase());

    const winColor = computed(() =>
      getColorStyle(adTemplate.value.h2hCard.winColor)
    );
    const drawColor = computed(() =>
      getColorStyle(adTemplate.value.h2hCard.drawColor)
    );
    const lossColor = computed(() =>
      getColorStyle(adTemplate.value.h2hCard.lossColor)
    );
    const borderColor = computed(() =>
      getColorStyle(adTemplate.value.h2hCard.historyBorderColor)
    );
    const textColor = computed(() =>
      getColorStyle(adTemplate.value.h2hCard.historyTextColor)
    );

    const classesRef = useStyles(
      computed(() => ({
        width: `${props.size + 4}px`,
        height: `${props.size}px`,
        fontSize: `${props.fontSize}px`,
        textColor: textColor.value,
        borderRadius: `${props.size / 2}px`
      }))
    );

    const { targetEl: historyEl } = composePopoverForm("Team Matches", [
      {
        type: "color",
        name: "Win color",
        get: () => adTemplate.value.h2hCard.winColor,
        set: (value: string) => {
          if (adTemplate) {
            adTemplate.value.h2hCard.winColor = value;
          }
        }
      },
      {
        type: "color",
        name: "Draw color",
        get: () => adTemplate.value.h2hCard.drawColor,
        set: (value: string) => {
          if (adTemplate) {
            adTemplate.value.h2hCard.drawColor = value;
          }
        }
      },
      {
        type: "color",
        name: "Loss color",
        get: () => adTemplate.value.h2hCard.lossColor,
        set: (value: string) => {
          if (adTemplate) {
            adTemplate.value.h2hCard.lossColor = value;
          }
        }
      },
      {
        type: "color",
        name: "Border color",
        get: () => adTemplate.value.h2hCard.historyBorderColor,
        set: (value: string) => {
          if (adTemplate) {
            adTemplate.value.h2hCard.historyBorderColor = value;
          }
        }
      },
      {
        type: "color",
        name: "Text color",
        get: () => adTemplate.value.h2hCard.historyTextColor,
        set: (value: string) => {
          if (adTemplate) {
            adTemplate.value.h2hCard.historyTextColor = value;
          }
        }
      }
    ]);

    return {
      event,
      historyEl,
      translation,
      winColor,
      drawColor,
      lossColor,
      borderColor,
      textColor,
      classes: classesRef
    };
  }
});
</script>
